import {
    getCurrentUser,
    signIn,
    signOut,
    AuthUser,
    resetPassword,
    confirmResetPassword,
    updatePassword,
    fetchAuthSession,
    confirmSignUp,
    resendSignUpCode,
} from "aws-amplify/auth";
import type {Authentication} from "../types/authentication";
import {CognitoErrorReason, ResourceException} from "../types/resourceError";
import {ResourceErrorType} from "../types/resourceError";
import {
    clearSessionActivity,
    getLastSessionActivity,
    isSessionIdleExpired,
    recordSessionActivity,
} from "../utils/sessionTimeoutUtils";

const cognitoUserToAuthenticationInfo = (authUser: AuthUser): Authentication => ({userId: authUser.userId});

const ensureSessionIsNotIdleExpired = async (): Promise<void> => {
    if (!isSessionIdleExpired()) {
        return;
    }

    await signOut();
    clearSessionActivity();

    throw new Error("Session expired due to inactivity.");
};

export const getUserAuthentication = async (): Promise<Authentication> =>
    ensureSessionIsNotIdleExpired()
        .then(() => getCurrentUser())
        .then((authUser) => {
            // Migration path for users who were already logged in
            // before this feature was deployed.
            if (getLastSessionActivity() === undefined) {
                recordSessionActivity();
            }

            return cognitoUserToAuthenticationInfo(authUser);
        })
        .catch(handleCognitoError);

export const userLogin = async (props: {username: string; password: string}) =>
    signIn(props)
        .catch(handleCognitoError)
        .then((result) => {
            if (result.isSignedIn) {
                recordSessionActivity();
                return result;
            } else {
                if (result.nextStep.signInStep === "CONFIRM_SIGN_UP") {
                    return throwCognitoResourceException(
                        "Email address is not confirmed. Please, confirm your email by clicking a link in invitational email.",
                        CognitoErrorReason.EMAIL_NOT_CONFIRMED,
                    );
                } else {
                    return throwCognitoResourceException(`Failed to log in. Reason: ${result.nextStep.signInStep}`);
                }
            }
        });

export const userLogout = async () => {
    await signOut();
    clearSessionActivity();
};
export const userResetPassword = async (email: string) => resetPassword({username: email}).catch(handleCognitoError);

export const confirmUserResetPassword = async (username: string, password: string, code: string) =>
    confirmResetPassword({username, newPassword: password, confirmationCode: code}).catch(handleCognitoError);

export const verifyEmail = async (username: string, code: string) =>
    confirmSignUp({username, confirmationCode: code}).catch(handleCognitoError);

export const resendVerificationEmail = async (username: string) =>
    resendSignUpCode({username}).catch(handleCognitoError);

export const getJwtToken = async (): Promise<string | undefined> =>
    ensureSessionIsNotIdleExpired()
        .then(() => fetchAuthSession())
        .then((session) => session.tokens?.accessToken.toString())
        .catch((error) => {
            // TODO: Handle User Authentication error

            throw error;
        })
        .catch(handleCognitoError);

export const changeUserPassword = async (props: {oldPassword: string; newPassword: string}) =>
    updatePassword(props).catch(handleCognitoError);

const handleCognitoError = (error: Error, reason?: CognitoErrorReason) =>
    throwCognitoResourceException(error.message, reason);

const throwCognitoResourceException = (message: string, reason?: CognitoErrorReason) => {
    throw new ResourceException(
        {
            type: ResourceErrorType.COGNITO_ERROR,
            reason: reason ?? CognitoErrorReason.UNKNOWN,
            message,
        },
        message,
    );
};
